import { z } from "zod";

// Super permissive text validation
const requiredText = z.string().optional().default("");

// Super permissive number validation
const requiredNumber = z.number().optional().nullable().default(null);

const optionalDate = z.preprocess((arg) => {
	if (arg === null || arg === undefined) return undefined;
	if (arg instanceof Date) return arg;
	try {
		return new Date(arg as string);
	} catch (e) {
		return undefined;
	}
}, z.date().optional());

const requiredUrl = z.string().url().optional().default("");

// Define the structure for each item in a repeatable group
const repeatableGroupItemSchema = z.record(z.any());

// Define the structure for a repeatable group with UUID keys
const repeatableGroupSchema = z.array(
	z.object({
		values: z.array(repeatableGroupItemSchema),
	}),
);

export type RepeatableGroup = z.infer<typeof repeatableGroupSchema>;

export const inputSettings = {
	"long-text": { validation: requiredText, default: "" },
	"short-text": { validation: requiredText, default: "" },
	number: { validation: requiredNumber, default: null },
	boolean: {
		validation: z.boolean().optional().default(false),
		default: false,
	},
	"yes-no": {
		validation: z.enum(["yes", "no"]).optional().nullable(),
		default: null,
	},
	duration: {
		validation: z
			.object({
				interval: requiredNumber,
				timeUnit: z.string().optional().default("day"),
			})
			.optional(),
		default: { interval: 1, timeUnit: "day" },
	},
	dropdown: { validation: z.string().optional().default(""), default: "" },
	email: { validation: z.string().optional().default(""), default: "" },
	"single-choice": { validation: requiredText, default: "" },
	"multiple-choice": {
		validation: z.array(z.string()).optional().default([]),
		default: [],
	},
	"scale-rating": { validation: requiredNumber, default: null },
	"visa-a-8": { validation: requiredNumber, default: null },
	"visa-g-8": { validation: requiredNumber, default: null },
	"visa-h-8": { validation: requiredNumber, default: null },
	"visa-p-8": { validation: requiredNumber, default: null },
	star: { validation: requiredNumber, default: null },
	slider: { validation: requiredNumber, default: null },
	"glucose-slider": { validation: requiredNumber, default: null },
	"pain-slider": { validation: requiredNumber, default: null },
	date: { validation: optionalDate, default: undefined },
	"date-picker": { validation: optionalDate, default: undefined },
	"date-time-picker": { validation: optionalDate, default: undefined },
	"month-year": { validation: optionalDate, default: "" },
	url: { validation: requiredUrl, default: "" },
	"file-upload": { validation: z.any(), default: null },
	"color-picker": {
		validation: z.string().optional().default("#000000"),
		default: "#000000",
	},
	"repeatable-group": {
		validation: repeatableGroupSchema.optional().default([]),
		default: [{ values: [] }],
	},
	"tried-treatment": {
		validation: z.object({
			hasTried: z.boolean().optional().default(false),
			duration: z.string().optional().default(""),
			isOngoing: z.enum(["yes", "no"]).optional().nullable(),
		}),
	},
};

export function getInputSchema(input: {
	input_key: string;
	is_optional: boolean;
}) {
	const settings = inputSettings[input.input_key as keyof typeof inputSettings];
	if (!settings) {
		throw new Error(`Unknown input type: ${input.input_key}`);
	}
	// Simply make everything optional
	return {
		validation: settings.validation,
		default: settings.default,
	};
}
